import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const API_BASE_URL = "http://127.0.0.1:3000/wandb";//'http://10.0.0.10:8000/wandb';

const ModelContext = createContext();

export const useModel = () => {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error('useModel must be used within a ModelProvider');
  }
  return context;
};

export const ModelProvider = ({ children, category }) => {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelConfig, setModelConfig] = useState({
    cls_names: [],
    model_name: '',
    model_architecture: '',
    loss_function: '',
    inference: '',
    tag: category
  });

  useEffect(() => {
    const fetchModels = async () => {
      if (category) {
        try {
          const response = await fetch(`${API_BASE_URL}/get-runs/${category}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            }
          });
          
          if (!response.ok) {
            //throw new Error('Failed to fetch models');
            const mockData = '["brisk-dream-36","magic-microwave-2","dashing-meadow-2","sparkling-monkey-3","gallant-frog-5","earnest-forest-7","honest-totem-9","logical-frog-10","floral-sound-12","lemon-oath-15","iconic-darkness-17","curious-snow-18","worthy-bird-22","polished-sponge-36","eternal-star-39","dulcet-voice-57","visionary-disco-58","boysenberry-flan-60"]'
            setModels(JSON.parse(mockData));
          } else {    
            const data = await response.json();
            console.log("Response Ok, data:", data);
            setModels(data);
          }
        } catch (err) {
          setError(err.message);
          console.error('Error fetching models:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchModels();
    }
  }, [category]);

  const fetchModelConfig = async (modelName) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/get-config/${modelName}/${category}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        //throw new Error('Failed to fetch model config');
        const mockData = '{"cls_names": ["normal_wood", "normal_wood_painted", "impregnated_wood", "impregnated_wood_painted", "hands", "background", "other"], "model_name": "mock_model", "model_architecture": "mock_architecture", "loss_function": "mock_loss", "inference": "mock_inference"}'
        setModelConfig(JSON.parse(mockData));
      } else {
        const data = await response.json();
        setModelConfig({
          cls_names: data.cls_names || [],
          model_name: data.model_name || '',
          model_architecture: data.model_architecture || '',
          loss_function: data.loss_function || '',
          inference: data.inference || '',
          tag: category
        });
      }
      
      setSelectedModel(modelName);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching model config:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModelConfigByUnit = async (unitId) => {
    try {
      setLoading(true);
      
      // Get the unit's deployment config from Firebase
      const unitRef = doc(db, 'units', unitId);
      console.log("Firebase call")
      const unitDoc = await getDoc(unitRef);
      
      if (!unitDoc.exists()) {
        throw new Error('Unit not found');
      }
      
      const deploymentConfig = unitDoc.data().deployment_config;
      if (!deploymentConfig || !deploymentConfig.model || !deploymentConfig.tag) {
        throw new Error('Unit does not have valid model configuration');
      }
      
      // Use the model_name and tag from the deployment config to fetch the model config
      const response = await fetch(`${API_BASE_URL}/get-config/${deploymentConfig.model_name}/${deploymentConfig.tag}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        //throw new Error('Failed to fetch model config');
        const mockData = '{"cls_names": ["normal_wood", "normal_wood_painted", "impregnated_wood", "impregnated_wood_painted", "hands", "background", "other"], "model_name": "mock_model", "model_architecture": "mock_architecture", "loss_function": "mock_loss", "inference": "mock_inference"}'
        const data = JSON.parse(mockData);
        setModelConfig({
          cls_names: data.cls_names || [],
          model_name: data.model_name || '',
          model_architecture: data.model_architecture || '',
          loss_function: data.loss_function || '',
          inference: data.inference || '',
          tag: deploymentConfig.tag
        });
      } else {
        const data = await response.json();
        setModelConfig({
          cls_names: data.cls_names || [],
          model_name: data.model_name || '',
          model_architecture: data.model_architecture || '',
          loss_function: data.loss_function || '',
          inference: data.inference || '',
          tag: deploymentConfig.tag
        });
      }
      
      setSelectedModel(deploymentConfig.model_name);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching model config by unit:', err);
    } finally {
      setLoading(false);
    }
  };

  const value = {
    models,
    loading,
    error,
    modelConfig,
    selectedModel,
    fetchModelConfig,
    fetchModelConfigByUnit
  };

  return (
    <ModelContext.Provider value={value}>
      {children}
    </ModelContext.Provider>
  );
};

export default ModelContext; 